/**
 * harness.ts — the 3-arm × multi-seed evaluation harness.
 *
 * Ports run_day_schedule + run_gradual_schedule from `_proto_reflex_v1.py`
 * (lines 715-747 + 683-712). Runs entirely inside a Web Worker so the UI
 * doesn't freeze during the ~30-60s evaluation.
 *
 * Two schedules:
 *   DISCRETE days: circles → squares → circles → blobs. Tests H1 (Day-0
 *     reflex) + H2 (within-day speedup). The day buffer resets at each
 *     boundary; long-term semantics survive.
 *   GRADUAL drift: circles → blobs over 32 worlds (smoothstep blend).
 *     Tests H3 (drift detection) — the EWMA detector should fire in the
 *     middle of the blend, not at the settled endpoints.
 *
 * Protocol (postMessage from the worker that hosts the harness):
 *   { kind: 'progress', arm, seed, schedule, worldIdx, nWorlds }
 *   { kind: 'done', discrete: DayResults[], gradual: GradualResults[], confusion }
 *   { kind: 'error', message }
 */

import {
  N_ACTIONS,
  N_FEATURES,
  DAY_SCHEDULE,
  N_WORLDS_PER_DAY,
  GRADUAL_SCHEDULE,
  N_GRADUAL_WORLDS,
  type ArmMode,
  type ShapeName,
} from './constants';
import { ReflexBandit } from './bandit';
import { ReflexEnv } from './environment';
import { makeCircles, makeSquares, makeBlobs, makeSpirals, makeBlended, type Grid } from './shapes';
import { makeRng } from './linalg';
import type { DriftStatus } from './constants';

export interface DayRow {
  dayIdx: number;
  worldInDay: number;
  shape: ShapeName;
  gold: number;
  total: number;
}

export interface DiscreteArmResult {
  arm: ArmMode;
  results: DayRow[]; // one per (day × world-in-day)
  driftEvents: Array<{ n: number; status: DriftStatus; err: number }>;
}

export interface GradualRow {
  worldIdx: number;
  alpha: number;
  gold: number;
  total: number;
}

export interface GradualArmResult {
  arm: ArmMode;
  results: GradualRow[];
  disagreeTimeline: Array<{ worldIdx: number; alpha: number; meanDis: number }>;
  driftEvents: Array<{ n: number; status: DriftStatus; err: number }>;
}

export interface ConfusionRow {
  phase: 'early' | 'mid' | 'late';
  chosenTok: number; // -1 OOB, 0 free, 1 gold, 2 stone
  count: number;
}

export interface HarnessConfig {
  seeds: number[];
  nWorldsPerDay: number;
  nGradualWorlds: number;
  minSamplesLeaf: number;
  nTrees: number;
  arm: ArmMode;
}

export const DEFAULT_HARNESS_CONFIG: HarnessConfig = {
  seeds: [0, 1, 2],
  nWorldsPerDay: N_WORLDS_PER_DAY,
  nGradualWorlds: N_GRADUAL_WORLDS,
  minSamplesLeaf: 10,
  nTrees: 40,
  arm: 'ts',
};

const SHAPES: Record<ShapeName, (rng: () => number) => Grid> = {
  circles: makeCircles,
  squares: makeSquares,
  blobs: makeBlobs,
  spirals: makeSpirals,
};

export type HarnessEvent =
  | { kind: 'progress'; arm: ArmMode; seed: number; schedule: 'discrete' | 'gradual'; worldIdx: number; nWorlds: number }
  | { kind: 'arm-done'; arm: ArmMode; schedule: 'discrete' | 'gradual' }
  | { kind: 'done'; discrete: DiscreteArmResult[]; gradual: GradualArmResult[]; confusion: ConfusionRow[] }
  | { kind: 'error'; message: string };

export type HarnessSink = (e: HarnessEvent) => void;

function runEpisode(env: ReflexEnv, agent: ReflexBandit, record: boolean): {
  gold: number;
  chosenTokCounts: Map<number, number>;
} {
  env.reset();
  const buf = new Float64Array(N_ACTIONS * N_FEATURES);
  const mask = new Uint8Array(N_ACTIONS);
  const destTokens = new Int8Array(N_ACTIONS);
  const initial = env.totalGold;
  const chosenTokCounts = new Map<number, number>();
  while (!env.done) {
    env.candidateFeatures(buf);
    env.candidateValid(mask);
    const { action } = agent.select(buf, mask);
    if (record) {
      env.candidateDestTokens(destTokens);
      const tok = destTokens[action];
      chosenTokCounts.set(tok, (chosenTokCounts.get(tok) || 0) + 1);
    }
    const xTaken = buf.slice(action * N_FEATURES, (action + 1) * N_FEATURES);
    const { reward, done } = env.step(action);
    agent.observe(xTaken, action, reward);
    if (done) break;
  }
  return { gold: initial - env.goldLeft, chosenTokCounts };
}

/** Run the discrete-day schedule for one (arm, seed). */
function runDaySchedule(arm: ArmMode, seed: number, cfg: HarnessConfig): DiscreteArmResult {
  const agent = new ReflexBandit({
    mode: arm,
    seed,
    minSamplesLeaf: cfg.minSamplesLeaf,
    nTrees: cfg.nTrees,
  });
  const results: DayRow[] = [];
  for (let dayIdx = 0; dayIdx < DAY_SCHEDULE.length; dayIdx++) {
    const shape = DAY_SCHEDULE[dayIdx];
    agent.resetDay();
    for (let w = 0; w < cfg.nWorldsPerDay; w++) {
      const wrng = makeRng(seed * 100_000 + dayIdx * 1000 + w);
      const world = SHAPES[shape](wrng);
      const env = new ReflexEnv(world);
      const { gold } = runEpisode(env, agent, false);
      results.push({ dayIdx, worldInDay: w, shape, gold, total: env.totalGold });
    }
  }
  return { arm, results, driftEvents: agent.driftEvents };
}

/** Run the gradual-drift schedule for one (arm, seed). */
function runGradualSchedule(arm: ArmMode, seed: number, cfg: HarnessConfig): GradualArmResult {
  const agent = new ReflexBandit({
    mode: arm,
    seed,
    minSamplesLeaf: cfg.minSamplesLeaf,
    nTrees: cfg.nTrees,
  });
  const results: GradualRow[] = [];
  const disagreeTimeline: GradualArmResult['disagreeTimeline'] = [];
  for (let w = 0; w < cfg.nGradualWorlds; w++) {
    const t = w / Math.max(1, cfg.nGradualWorlds - 1);
    const alpha = t * t * (3 - 2 * t); // smoothstep
    const wrng = makeRng(seed * 100_000 + w);
    const world = makeBlended(wrng, GRADUAL_SCHEDULE[0], GRADUAL_SCHEDULE[1], alpha);
    const env = new ReflexEnv(world);
    const record = w % 4 === 0;
    const { gold } = runEpisode(env, agent, record);
    results.push({ worldIdx: w, alpha, gold, total: env.totalGold });
    if (record) {
      const buf = new Float64Array(N_ACTIONS * N_FEATURES);
      env.candidateFeatures(buf);
      disagreeTimeline.push({ worldIdx: w, alpha, meanDis: agent.lastDisagreement(buf) });
    }
  }
  const driftEvents = agent.driftEvents;
  return { arm, results, disagreeTimeline, driftEvents };
}

/**
 * Run the confusion diagnostic for one arm: classify every step's chosen
 * destination token into {GOLD, FREE, STONE/OOB}, pooled across three
 * training phases (early / mid / late), aggregated over all seeds.
 *
 * "Early" = first 1/3 of worlds; "mid" = middle 1/3; "late" = last 1/3.
 * A trained agent shifts toward GOLD and away from STONE/OOB across phases.
 */
function runConfusion(arm: ArmMode, cfg: HarnessConfig): ConfusionRow[][] {
  // Three phases, each a map: chosenTok -> count.
  const phaseCounts: Array<Map<number, number>> = [
    new Map(),
    new Map(),
    new Map(),
  ];
  const totalWorlds = DAY_SCHEDULE.length * cfg.nWorldsPerDay;
  for (const seed of cfg.seeds) {
    const agent = new ReflexBandit({
      mode: arm,
      seed,
      minSamplesLeaf: cfg.minSamplesLeaf,
      nTrees: cfg.nTrees,
    });
    let worldIdx = 0;
    for (let dayIdx = 0; dayIdx < DAY_SCHEDULE.length; dayIdx++) {
      agent.resetDay();
      for (let w = 0; w < cfg.nWorldsPerDay; w++) {
        const wrng = makeRng(seed * 100_000 + dayIdx * 1000 + w);
        const world = SHAPES[DAY_SCHEDULE[dayIdx]](wrng);
        const env = new ReflexEnv(world);
        const phase = worldIdx < totalWorlds / 3 ? 0 : worldIdx < (2 * totalWorlds) / 3 ? 1 : 2;
        const { chosenTokCounts } = runEpisode(env, agent, true);
        for (const [tok, count] of chosenTokCounts) {
          phaseCounts[phase].set(tok, (phaseCounts[phase].get(tok) || 0) + count);
        }
        worldIdx++;
      }
    }
  }
  return phaseCounts.map((counts, phase) => {
    const rows: ConfusionRow[] = [];
    const phaseLabel: ConfusionRow['phase'] = phase === 0 ? 'early' : phase === 1 ? 'mid' : 'late';
    for (const tok of [-1, 0, 1, 2]) {
      rows.push({ phase: phaseLabel, chosenTok: tok, count: counts.get(tok) || 0 });
    }
    return rows;
  });
}

/**
 * The full harness. Runs all 3 arms × all seeds on both schedules + the
 * confusion diagnostic. Posts progress events to `sink` as it goes.
 */
export function runHarness(cfg: HarnessConfig, sink: HarnessSink): void {
  try {
    const discrete: DiscreteArmResult[] = [];
    const gradual: GradualArmResult[] = [];
    const arms: ArmMode[] = ['ts', 'greedy', 'random'];
    for (const arm of arms) {
      const armCfg = { ...cfg, arm };
      // Discrete: aggregate across seeds.
      const discPerSeed: DiscreteArmResult[] = [];
      for (const seed of cfg.seeds) {
        // Signal progress at world granularity (we can't post per-world
        // easily without refactoring runDaySchedule; post per-seed instead).
        discPerSeed.push(runDaySchedule(arm, seed, armCfg));
        sink({
          kind: 'progress',
          arm,
          seed,
          schedule: 'discrete',
          worldIdx: cfg.seeds.indexOf(seed) + 1,
          nWorlds: cfg.seeds.length,
        });
      }
      discrete.push(mergeDiscrete(arm, discPerSeed));
      sink({ kind: 'arm-done', arm, schedule: 'discrete' });

      // Gradual: aggregate across seeds.
      const gradPerSeed: GradualArmResult[] = [];
      for (const seed of cfg.seeds) {
        gradPerSeed.push(runGradualSchedule(arm, seed, armCfg));
        sink({
          kind: 'progress',
          arm,
          seed,
          schedule: 'gradual',
          worldIdx: cfg.seeds.indexOf(seed) + 1,
          nWorlds: cfg.seeds.length,
        });
      }
      gradual.push(mergeGradual(arm, gradPerSeed));
      sink({ kind: 'arm-done', arm, schedule: 'gradual' });
    }

    // Confusion matrix: TS only (the one arm that learns). Three phases
    // (early/mid/late), each with token-bucket counts. Flattened to a single
    // ConfusionRow[] for the main-thread renderer.
    const confusion = runConfusion('ts', cfg).flat();

    sink({ kind: 'done', discrete, gradual, confusion });
  } catch (e) {
    sink({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
  }
}

function mergeDiscrete(arm: ArmMode, perSeed: DiscreteArmResult[]): DiscreteArmResult {
  // Average gold per (day, world-in-day) across seeds.
  const byKey = new Map<string, { sumGold: number; sumTotal: number; n: number; shape: ShapeName }>();
  for (const r of perSeed) {
    for (const row of r.results) {
      const key = `${row.dayIdx}-${row.worldInDay}`;
      const entry = byKey.get(key) || { sumGold: 0, sumTotal: 0, n: 0, shape: row.shape };
      entry.sumGold += row.gold;
      entry.sumTotal += row.total;
      entry.n++;
      byKey.set(key, entry);
    }
  }
  const results: DayRow[] = [];
  for (const [key, entry] of byKey) {
    const [dayIdx, worldInDay] = key.split('-').map(Number);
    results.push({
      dayIdx,
      worldInDay,
      shape: entry.shape,
      gold: entry.sumGold / entry.n,
      total: entry.sumTotal / entry.n,
    });
  }
  results.sort((a, b) => a.dayIdx - b.dayIdx || a.worldInDay - b.worldInDay);
  const driftEvents = perSeed.flatMap((r) => r.driftEvents);
  return { arm, results, driftEvents };
}

function mergeGradual(arm: ArmMode, perSeed: GradualArmResult[]): GradualArmResult {
  const byKey = new Map<number, { sumGold: number; sumTotal: number; n: number; alpha: number }>();
  for (const r of perSeed) {
    for (const row of r.results) {
      const entry = byKey.get(row.worldIdx) || { sumGold: 0, sumTotal: 0, n: 0, alpha: row.alpha };
      entry.sumGold += row.gold;
      entry.sumTotal += row.total;
      entry.n++;
      byKey.set(row.worldIdx, entry);
    }
  }
  const results: GradualRow[] = [];
  for (const [worldIdx, entry] of byKey) {
    results.push({
      worldIdx,
      alpha: entry.alpha,
      gold: entry.sumGold / entry.n,
      total: entry.sumTotal / entry.n,
    });
  }
  results.sort((a, b) => a.worldIdx - b.worldIdx);
  const disagreeTimeline = perSeed[0]?.disagreeTimeline || [];
  const driftEvents = perSeed.flatMap((r) => r.driftEvents);
  return { arm, results, disagreeTimeline, driftEvents };
}
